import React, { Fragment, useMemo, useState } from "react";
import { Table, Row, Button } from "reactstrap";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FadeLoader } from "react-spinners";
import Select from "react-select";
import { Eye } from "react-feather";
import { DebouncedInput } from "helpers/common_helper";
import Pagination from "components/Common/Pagination";
import { formatDateTimeToAmPm } from "helpers/dateFormat_helper";
import SubmissionDetailsModal from "./SubmissionDetailsModal";


const AssignmentSubmissionTable = ({
  submissions = [],
  loading,
  totalrows,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  searchString,
  setSearchString,
  selectedStatus,
  setSelectedStatus,
  selectedSortData,
  setSelectedSortData,
}) => {
  // 🔹 Modal state
  const [openModal, setOpenModal] = useState(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);

  // 🔹 Handle view submission
  const handleView = (id) => {
    setSelectedSubmissionId(id);
    setOpenModal(true);
  };

  // 🔹 Column Definitions
  const columns = useMemo(
    () => [
      {
        header: "#",
        id: "serial",
        cell: ({ row }) => (pageIndex - 1) * pageSize + row.index + 1,
      },
      { header: "Student", accessorKey: "studentName" },
      { header: "Email", accessorKey: "email" },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.status === "reviewed"
                ? "bg-success"
                : "bg-warning text-dark"
            }`}
          >
            {row.original.status || "-"}
          </span>
        ),
      },
      { header: "Marks", accessorKey: "marks" },
      {
        header: "Comment",
        accessorKey: "comment",
        cell: ({ row }) =>
          row.original.comment?.length > 50
            ? row.original.comment.slice(0, 50) + "..."
            : row.original.comment || "-",
      },
      {
        header: "Submitted At",
        accessorKey: "submittedAt",
        cell: ({ row }) => formatDateTimeToAmPm(row.original.submittedAt),
      },
      {
        header: "Reviewed At",
        accessorKey: "reviewedAt",
        cell: ({ row }) =>
          row.original.reviewedAt
            ? formatDateTimeToAmPm(row.original.reviewedAt)
            : "-",
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }) => (
          <Button
            color="info"
            size="sm"
            onClick={() => handleView(row.original._id)}
            title="View Submission"
          >
            <Eye size={16} />
          </Button>
        ),
      },
    ],
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    columns,
    data: submissions || [],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const totalPages = Math.ceil(totalrows / pageSize);
  const handlePageChange = (newPageIndex) => setPageIndex(newPageIndex);

  const statusOptions = [
    { label: "All", value: "" },
    { label: "Reviewed", value: "reviewed" },
    { label: "Pending", value: "pending" },
  ];

  return (
    <Fragment>
      {/* 🔹 Filters */}
      <Row className="mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap w-100">
          {/* Left Filters */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* Filter by Status */}
            <div style={{ width: "220px" }}>
              <Select
                placeholder="Filter by Status"
                value={selectedStatus}
                onChange={setSelectedStatus}
                options={statusOptions}
                isClearable
              />
            </div>
          </div>

          {/* Search */}
          <div style={{ width: "250px" }}>
            <DebouncedInput
              value={searchString ?? ""}
              onChange={(value) => setSearchString(String(value))}
              className="form-control"
              placeholder="Search student..."
            />
          </div>
        </div>
      </Row>

      {/* 🔹 Table */}
      <div className="table-responsive">
        <Table bordered hover>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </span>
                      {header.column.columnDef.accessorKey && (
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            const field = header.column.columnDef.accessorKey;
                            if (!field) return;
                            if (
                              !selectedSortData ||
                              selectedSortData.value !== field
                            )
                              setSelectedSortData({
                                value: field,
                                direction: "asc",
                              });
                            else if (selectedSortData.direction === "asc")
                              setSelectedSortData({
                                value: field,
                                direction: "desc",
                              });
                            else
                              setSelectedSortData({
                                value: field,
                                direction: "asc",
                              });
                          }}
                        >
                          {selectedSortData?.value ===
                          header.column.columnDef.accessorKey ? (
                            selectedSortData.direction === "asc" ? (
                              <i className="mdi mdi-arrow-up"></i>
                            ) : (
                              <i className="mdi mdi-arrow-down"></i>
                            )
                          ) : (
                            <i className="mdi mdi-swap-vertical"></i>
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} style={{ height: "120px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <FadeLoader color="#00a895" size={40} />
                  </div>
                </td>
              </tr>
            ) : submissions?.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No submissions found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {totalrows > 0 && (
        <Pagination
          currentPage={pageIndex}
          totalPages={totalPages}
          totalItems={totalrows}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          showInfo
        />
      )}

      {/* 🔹 View Submission Modal */}
      <SubmissionDetailsModal
        isOpen={openModal}
        toggle={() => setOpenModal(false)}
        submissionId={selectedSubmissionId}
      />
    </Fragment>
  );
};

export default AssignmentSubmissionTable;
